/**
 * "There's a gateway here you probably want to adopt" notification.
 *
 * Once a user has granted Local Network Access, the app-wide beacon keeps
 * watching at a 60s cadence. When something unclaimed turns up while they're
 * off doing something else, say so — plugging in an appliance and having the
 * app notice unprompted is the whole payoff for granting the permission.
 *
 * An in-app toast, deliberately: asking for the Notifications permission to
 * solve a permission problem would be absurd, and the action can only be taken
 * inside the app anyway.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useAuth } from './useAuth';
import { useGatewayClaim } from './useGatewayClaim';
import { accountScoped } from '@/lib/activeAccount';

const SUPPRESS_KEY = 'netman.gatewayDiscoveryToast.dismissed';
/** How long "Not now" lasts. Long enough that a gateway nobody wants stops nagging. */
const SUPPRESS_MS = 7 * 24 * 60 * 60 * 1000;

type SuppressionMap = Record<string, number>;

function readSuppressions(): SuppressionMap {
  try {
    const raw = window.localStorage.getItem(accountScoped(SUPPRESS_KEY));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as SuppressionMap;
    const now = Date.now();
    // Drop expired entries on read so the key can't grow without bound.
    return Object.fromEntries(Object.entries(parsed).filter(([, until]) => until > now));
  } catch {
    return {};
  }
}

function suppress(hardwareId: string) {
  try {
    const next = { ...readSuppressions(), [hardwareId]: Date.now() + SUPPRESS_MS };
    window.localStorage.setItem(accountScoped(SUPPRESS_KEY), JSON.stringify(next));
  } catch {
    // Private mode / quota. Worst case the toast reappears next session.
  }
}

/**
 * Watch the shared beacon and toast on newly discovered gateways.
 *
 * Mount once, app-wide, inside the claim provider.
 */
export function useGatewayDiscoveryToast() {
  const { discoveredGateways } = useGatewayClaim();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Gateways we've already toasted about in this tab, so a 60s poll that keeps
  // returning the same gateway doesn't keep re-announcing it.
  const announced = useRef<Set<string>>(new Set());

  const onGatewaysPage = location.pathname.startsWith('/gateways');

  const handleClaim = useCallback(() => { void navigate('/gateways'); }, [navigate]);

  useEffect(() => {
    // The scan panel is already showing these in place — a toast would be noise.
    if (onGatewaysPage) return;

    const suppressed = readSuppressions();

    for (const gateway of discoveredGateways) {
      const key = gateway.hardwareId || gateway.sessionId;
      if (announced.current.has(key) || suppressed[key]) continue;
      announced.current.add(key);

      // Name the context the claim would land in. The beacon follows the
      // browser, not the tenant: an MSP tech sitting at one client's site with
      // another client's context selected must be able to see that before they
      // act on it. (The claim form itself still asks for name/region/division —
      // this is never a one-click adopt.)
      const target = user?.activeContext?.name;

      toast('New gateway found on this network', {
        id: `gateway-discovered-${key}`,
        description: [
          gateway.serial ? `SN ${gateway.serial}` : null,
          gateway.localIp,
          target ? `Ready to add to ${target}` : null,
        ]
          .filter(Boolean)
          .join(' · '),
        // A decision, not a status update — it shouldn't evaporate mid-read.
        duration: Infinity,
        action: { label: 'Claim it', onClick: handleClaim },
        cancel: { label: 'Not now', onClick: () => { suppress(key); } },
      });
    }
  }, [discoveredGateways, onGatewaysPage, user?.activeContext?.name, handleClaim]);
}
